import type { Locale, LocaleMetadata } from "@src/i18n/locale-catalog";
import { localeMetadata } from "@src/i18n/locale-catalog";

export function getAlternateLocaleUrl(
  currentUrl: string,
  alternateLocaleUrl: string,
  workspacePayload?: string,
): string {
  const current = new URL(currentUrl);
  const alternate = new URL(alternateLocaleUrl, current);
  alternate.search = "";
  alternate.hash =
    workspacePayload === undefined ? "" : `s=${workspacePayload}`;
  return alternate.href;
}

export function getAppRootUrl(currentUrl: string, appRootUrl: string): URL {
  return new URL(appRootUrl, currentUrl);
}

export function synchronizeLocaleFromLocation(
  onLocaleChange: (locale: Locale) => void,
): boolean {
  const root = appRootUrl();
  const pathname = window.location.pathname;
  const metadata = localeMetadata.find(
    ({ path }) => new URL(path, root).pathname === pathname,
  );
  if (
    !metadata ||
    [metadata.languageTag, ...metadata.languageAliases].some(
      (tag) =>
        tag.toLowerCase() === document.documentElement.lang.toLowerCase(),
    )
  )
    return false;

  applyLocaleMetadata(metadata, root);
  onLocaleChange(metadata.code);
  return true;
}

export function navigateToLocale({
  next,
  current,
}: {
  next: Locale;
  current: Locale;
}): boolean {
  if (next === current) return false;
  const metadata = localeMetadata.find(({ code }) => code === next);
  if (!metadata) return false;
  const root = appRootUrl();
  const url = getAlternateLocaleUrl(
    window.location.href,
    new URL(metadata.path, root).href,
  );
  window.history.replaceState(window.history.state, "", url);
  applyLocaleMetadata(metadata, root);
  return true;
}

export function appRootUrl(): URL {
  return getAppRootUrl(
    window.location.href,
    document.documentElement.dataset.appRootUrl ?? import.meta.env.BASE_URL,
  );
}

function applyLocaleMetadata(metadata: LocaleMetadata, root: URL): void {
  document.documentElement.lang = metadata.languageTag;
  document.documentElement.dataset.appRootUrl = root.href;
  document.title = metadata.page.title;
  setMetaContent('meta[name="description"]', metadata.page.description);
  setMetaContent(
    'meta[name="application-name"]',
    metadata.page.applicationName,
  );
  setMetaContent(
    'meta[property="og:site_name"]',
    metadata.page.openGraphSiteName,
  );
  setMetaContent('meta[property="og:title"]', metadata.page.title);
  setMetaContent('meta[property="og:description"]', metadata.page.description);
  synchronizeOpenGraphLocales(metadata);
  const manifest = document.querySelector<HTMLLinkElement>(
    'link[rel="manifest"]',
  );
  if (manifest) manifest.href = new URL(metadata.manifestPath, root).href;
  setLinkHref('link[rel="icon"]', "favicon.svg", root);
  setLinkHref('link[rel="apple-touch-icon"]', "apple-touch-icon.png", root);
}

function synchronizeOpenGraphLocales(metadata: LocaleMetadata): void {
  const primary = document.querySelector<HTMLMetaElement>(
    'meta[property="og:locale"]',
  );
  if (primary) primary.content = metadata.page.openGraphLocale;

  document
    .querySelectorAll('meta[property="og:locale:alternate"]')
    .forEach((alternate) => {
      alternate.remove();
    });

  let insertionPoint: Element | null = primary;
  for (const alternate of localeMetadata.filter(
    ({ code }) => code !== metadata.code,
  )) {
    const element = document.createElement("meta");
    element.setAttribute("property", "og:locale:alternate");
    element.content = alternate.page.openGraphLocale;
    if (insertionPoint)
      insertionPoint.insertAdjacentElement("afterend", element);
    else document.head.append(element);
    insertionPoint = element;
  }
}

function setLinkHref(selector: string, path: string, root: URL): void {
  const link = document.querySelector<HTMLLinkElement>(selector);
  if (link) link.href = new URL(path, root).href;
}

function setMetaContent(selector: string, content: string): void {
  const meta = document.querySelector<HTMLMetaElement>(selector);
  if (meta) meta.content = content;
}
